import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, LucideIcon } from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

export interface ModeOption<T extends string = string> {
  id: T;
  label: string;
  description?: string;
  icon: LucideIcon;
  iconColor?: string;
  bgColor?: string;
}

interface ModeSelectorProps<T extends string = string> {
  options: ModeOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  disabled?: boolean;
  compact?: boolean;
}

// ============================================================================
// COMPOSANT MODE SELECTOR (VS Code style)
// ============================================================================

export function ModeSelector<T extends string = string>({
  options,
  value,
  onChange,
  className = '',
  disabled = false,
  compact = false
}: ModeSelectorProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const selectedOption = options.find(opt => opt.id === value) || options[0];
  const Icon = selectedOption.icon;

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fermer avec Escape
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  const handleSelect = (optionId: T) => {
    onChange(optionId);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center gap-2 px-3 py-1.5 rounded-lg
          border border-border bg-background
          hover:bg-muted/50 transition-all
          focus:outline-none focus:ring-2 focus:ring-primary/20
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${compact ? 'text-xs' : 'text-sm'}
        `}
      >
        <Icon className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} ${selectedOption.iconColor || 'text-primary'}`} />
        <span className="font-medium text-foreground">{selectedOption.label}</span>
        <ChevronDown className={`
          ${compact ? 'w-3 h-3' : 'w-4 h-4'} text-muted-foreground
          transition-transform duration-200
          ${isOpen ? 'rotate-180' : ''}
        `} />
      </button>

      {/* Dropdown Menu - S'ouvre vers le haut */}
      {isOpen && (
        <div className={`
          absolute z-[100] bottom-full mb-1 w-56
          bg-popover border border-border rounded-lg shadow-lg
          animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2
          duration-150
        `}>
          <div className="p-1">
            {options.map((option) => {
              const OptionIcon = option.icon;
              const isSelected = option.id === value;
              
              return (
                <button
                  key={option.id}
                  onClick={() => handleSelect(option.id)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 rounded-md
                    text-left transition-colors
                    ${isSelected 
                      ? 'bg-primary/10 text-primary' 
                      : 'hover:bg-muted text-foreground'
                    }
                  `}
                >
                  <div className={`
                    flex-shrink-0 p-1.5 rounded-md
                    ${option.bgColor || 'bg-muted'}
                  `}>
                    <OptionIcon className={`w-4 h-4 ${option.iconColor || 'text-foreground'}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium text-sm ${isSelected ? 'text-primary' : ''}`}>
                        {option.label}
                      </span>
                      {isSelected && (
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      )}
                    </div>
                    {option.description && (
                      <p className="text-xs text-muted-foreground truncate">
                        {option.description}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
